import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { showToast } from "../../assets/util/showToast";
import { api } from "../../services/api";

const ChangePassword: React.FC = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });
  const [errors, setErrors] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error for this field
    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validate = (): boolean => {
    const newErrors = {
      current_password: "",
      new_password: "",
      confirm_password: "",
    };
    let isValid = true;

    if (!formData.current_password) {
      newErrors.current_password = "Current password is required";
      isValid = false;
    }

    if (!formData.new_password) {
      newErrors.new_password = "New password is required";
      isValid = false;
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = "Password must be at least 8 characters";
      isValid = false;
    }

    if (!formData.confirm_password) {
      newErrors.confirm_password = "Please confirm your new password";
      isValid = false;
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = "Passwords do not match";
      isValid = false;
    }

    if (formData.current_password === formData.new_password) {
      newErrors.new_password =
        "New password must be different from current password";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setLoading(true);

    try {
      const response = await api.post<{
        success: boolean;
        message: string;
      }>("/change-password.php", {
        current_password: formData.current_password,
        new_password: formData.new_password,
        confirm_password: formData.confirm_password,
      });

      if (response.success) {
        showToast(
          response.message || "Password changed successfully!",
          "success",
        );

        // Clear form
        setFormData({
          current_password: "",
          new_password: "",
          confirm_password: "",
        });

        // Logout user and redirect to login after 2 seconds
        setTimeout(() => {
          logout();
          navigate("/account/login");
        }, 2000);
      } else {
        showToast(response.message || "Failed to change password", "error");
      }
    } catch (error: any) {
      const errorMessage =
        error.message || "An error occurred while changing password";
      showToast(errorMessage, "error");

      // If it's a "current password incorrect" error, highlight that field
      if (errorMessage.toLowerCase().includes("current password")) {
        setErrors((prev) => ({
          ...prev,
          current_password: errorMessage,
        }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="change-password-page">
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Change Password</h3>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            <div className="mb-4">
              <label htmlFor="current_password" className="form-label required">
                Current Password
              </label>
              <input
                type="password"
                className={`form-control ${
                  errors.current_password ? "is-invalid" : ""
                }`}
                id="current_password"
                name="current_password"
                value={formData.current_password}
                onChange={handleChange}
                placeholder="Enter your current password"
                autoComplete="current-password"
              />
              {errors.current_password && (
                <div className="invalid-feedback">
                  {errors.current_password}
                </div>
              )}
            </div>

            <div className="mb-4">
              <label htmlFor="new_password" className="form-label required">
                New Password
              </label>
              <input
                type="password"
                className={`form-control ${errors.new_password ? "is-invalid" : ""}`}
                id="new_password"
                name="new_password"
                value={formData.new_password}
                onChange={handleChange}
                placeholder="Enter your new password (min. 8 characters)"
                autoComplete="new-password"
              />
              {errors.new_password && (
                <div className="invalid-feedback">{errors.new_password}</div>
              )}
              <div className="form-text">
                Password must be at least 8 characters long
              </div>
            </div>

            <div className="mb-4">
              <label htmlFor="confirm_password" className="form-label required">
                Confirm New Password
              </label>
              <input
                type="password"
                className={`form-control ${
                  errors.confirm_password ? "is-invalid" : ""
                }`}
                id="confirm_password"
                name="confirm_password"
                value={formData.confirm_password}
                onChange={handleChange}
                placeholder="Confirm your new password"
                autoComplete="new-password"
              />
              {errors.confirm_password && (
                <div className="invalid-feedback">
                  {errors.confirm_password}
                </div>
              )}
            </div>

            <div className="d-flex gap-3">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Changing Password...
                  </>
                ) : (
                  "Change Password"
                )}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => navigate(-1)}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>

          <div className="alert alert-info mt-4">
            <i className="bi bi-info-circle me-2"></i>
            After changing your password, you will be automatically logged out
            and need to login again with your new password.
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
